import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import SEO from '../components/SEO';

const BlogIndexPage = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        const res = await fetch('/blog/index.json');
        if (!res.ok) throw new Error('Failed to load blog index');
        const data = await res.json();
        if (active) {
          setPosts(data.posts || []);
          setError(null);
        }
      } catch (err) {
        if (active) setError('Unable to load posts right now.');
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => {
      active = false;
    };
  }, []);

  const sortedPosts = useMemo(() => {
    return [...posts].sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));
  }, [posts]);

  const featuredPost = sortedPosts[0];
  const archivePosts = sortedPosts.slice(1);

  return (
    <Shell>
      <SEO
        title="Blog"
        description="Notes, updates, and thinking from the Noteician team."
        canonicalPath="/blog"
      />

      <Layout>
        <TopBar>
          <LogoLink to="/">Noteician</LogoLink>
          <TopAction to="/contact">Contact</TopAction>
        </TopBar>

        <Header>
          <Kicker>Field Notes</Kicker>
          <Title>Signal over noise.</Title>
          <Subtitle>
            From the Noteician team—real-world rollouts, workflow experiments,
            and practical playbooks for calmer, cleaner clinical documentation.
          </Subtitle>
        </Header>

        {loading && <Status>Loading posts…</Status>}
        {error && <Status>{error}</Status>}
        {!loading && !error && sortedPosts.length === 0 && (
          <Status>No posts yet. Check back soon.</Status>
        )}

        {!loading && !error && featuredPost && (
          <FeaturedCard to={`/blog/${featuredPost.slug}`}>
            <FeaturedLabel>Latest</FeaturedLabel>
            <FeaturedTitle>{featuredPost.title}</FeaturedTitle>
            {featuredPost.date && (
              <MetaLine>
                {new Date(featuredPost.date).toLocaleDateString(undefined, {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </MetaLine>
            )}
            {featuredPost.description && (
              <FeaturedDesc>
                <span>{featuredPost.description}</span>
              </FeaturedDesc>
            )}
            <FeaturedAction>
              Read the story <Arrow>→</Arrow>
            </FeaturedAction>
          </FeaturedCard>
        )}

        {!loading && !error && sortedPosts.length > 0 && (
          <>
            <SectionHead>
              <SectionTitle>All notes</SectionTitle>
              <SectionRule />
            </SectionHead>

            {archivePosts.length === 0 ? (
              <Empty>More posts are landing soon.</Empty>
            ) : (
              <Grid>
                {archivePosts.map((post, idx) => (
                  <Card key={post.slug} to={`/blog/${post.slug}`} $tall={idx % 3 === 0}>
                    <CardMeta>
                      {post.date
                        ? new Date(post.date).toLocaleDateString(undefined, {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric'
                        })
                        : 'Fresh note'}
                    </CardMeta>
                    <CardTitle>{post.title}</CardTitle>
                    {post.description && <CardDesc>{post.description}</CardDesc>}
                    <CardAction>
                      Read <Arrow>→</Arrow>
                    </CardAction>
                  </Card>
                ))}
              </Grid>
            )}
          </>
        )}
      </Layout>
    </Shell>
  );
};

const Shell = styled.div`
  min-height: 100vh;
  background:
    linear-gradient(180deg, rgba(247, 245, 242, 0.9), rgba(247, 245, 242, 0.9)),
    repeating-linear-gradient(90deg, rgba(0, 0, 0, 0.015) 0, rgba(0, 0, 0, 0.015) 1px, transparent 1px, transparent 18px),
    repeating-linear-gradient(0deg, rgba(0, 0, 0, 0.012) 0, rgba(0, 0, 0, 0.012) 1px, transparent 1px, transparent 16px);
  color: var(--black);
`;

const Layout = styled.div`
  max-width: 1080px;
  margin: 0 auto;
  padding: 30px 22px 120px;
`;

const TopBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-bottom: 26px;
`;

const LogoLink = styled(Link)`
  font-weight: 800;
  letter-spacing: 0.4px;
  color: var(--black);
  text-decoration: none;
  padding: 9px 14px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.92);
`;

const TopAction = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: transparent;
  color: var(--black);
  border-radius: 999px;
  font-weight: 700;
  text-decoration: none;
  border: 1px solid rgba(0, 0, 0, 0.1);
  transition: background 0.2s ease, transform 0.2s ease;

  &:hover {
    background: rgba(0, 0, 0, 0.04);
    transform: translateY(-1px);
  }
`;

const Header = styled.header`
  max-width: 920px;
  margin: 24px 0 30px;
  padding: 0 24px;
`;

const Kicker = styled.div`
  text-transform: uppercase;
  letter-spacing: 0.12em;
  font-size: 0.78rem;
  color: var(--primary);
  margin-bottom: 10px;
  font-weight: 700;
`;

const Title = styled.h1`
  font-size: 2.9rem;
  margin: 0 0 12px;
  color: var(--black);
  letter-spacing: -0.01em;
  font-family: 'Georgia', 'Times New Roman', serif;
`;

const Subtitle = styled.p`
  font-size: 1.1rem;
  color: var(--dark-gray);
  margin: 0;
  max-width: 720px;
  line-height: 1.7;
`;

const Status = styled.div`
  color: var(--dark-gray);
  padding: 8px 24px 14px;
  font-weight: 600;
`;

const FeaturedCard = styled(Link)`
  display: grid;
  gap: 8px;
  padding: 20px 24px 22px;
  margin: 14px 0 28px;
  background: #ffffff;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 16px;
  text-decoration: none;
  color: inherit;
  position: relative;
  overflow: hidden;
  box-shadow:
    0 10px 22px rgba(0, 0, 0, 0.06),
    inset 0 1px 0 rgba(255, 255, 255, 0.7);
  transition: transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease;

  &:before {
    content: '';
    position: absolute;
    top: -60px;
    inset-inline-end: -80px;
    width: 200px;
    height: 200px;
    background: radial-gradient(circle, rgba(0, 128, 128, 0.08), transparent 60%);
    opacity: 0.7;
  }

  &:hover {
    transform: translateY(-3px);
    border-color: rgba(0, 0, 0, 0.12);
    box-shadow: 0 16px 34px rgba(0, 0, 0, 0.08);
  }
`;

const FeaturedLabel = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  width: fit-content;
  padding: 7px 10px;
  background: rgba(0, 0, 0, 0.04);
  border-radius: 999px;
  font-weight: 700;
  color: var(--primary-dark);
  letter-spacing: 0.04em;
  text-transform: uppercase;
`;

const FeaturedTitle = styled.h2`
  font-size: 1.7rem;
  margin: 2px 0 4px;
  color: var(--black);
  font-family: 'Georgia', 'Times New Roman', serif;
`;

const MetaLine = styled.div`
  color: var(--dark-gray);
  font-size: 0.98rem;
`;

const FeaturedDesc = styled.div`
  margin: 6px 0 6px;
  color: var(--dark-gray);
  line-height: 1.65;

  span:first-child::first-letter {
    font-size: 2.6rem;
    float: left;
    line-height: 1;
    padding-inline-end: 10px;
    color: var(--primary-dark);
    font-family: 'Georgia', 'Times New Roman', serif;
  }
`;

const FeaturedAction = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: var(--primary-dark);
  font-weight: 700;
`;

const SectionHead = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 24px 12px;
`;

const SectionTitle = styled.h3`
  margin: 0;
  font-size: 1rem;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: var(--primary-dark);
`;

const SectionRule = styled.div`
  flex: 1;
  height: 1px;
  background: rgba(0, 0, 0, 0.08);
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 16px;
  padding: 0 24px;
`;

const Card = styled(Link)`
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.06);
  border-radius: 16px;
  padding: ${({ $tall }) => ($tall ? '22px 18px 22px' : '18px 18px 20px')};
  text-decoration: none;
  color: inherit;
  box-shadow: 0 10px 38px rgba(0, 0, 0, 0.06);
  transition: transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease;

  &:hover {
    transform: translateY(-4px);
    border-color: rgba(0, 128, 128, 0.28);
    box-shadow: 0 16px 44px rgba(0, 128, 128, 0.18);
  }
`;

const CardMeta = styled.div`
  color: var(--dark-gray);
  font-size: 0.95rem;
  margin-bottom: 10px;
`;

const CardTitle = styled.h4`
  font-size: 1.25rem;
  margin: 0 0 10px;
  color: var(--black);
`;

const CardDesc = styled.p`
  margin: 0 0 12px;
  color: var(--dark-gray);
  line-height: 1.5;
`;

const CardAction = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: var(--primary);
  font-weight: 700;
`;

const Empty = styled.div`
  padding: 12px 24px 32px;
  color: var(--dark-gray);
  font-weight: 600;
`;

const Arrow = styled.span`
  font-weight: 800;
  color: var(--primary);
  margin-inline-start: 4px;
`;

export default BlogIndexPage;
